import { Graphics } from 'models/style';

const parseHSL = (hslString: string): { h: number; s: number; l: number } => {
  const match = hslString.match(/hsl\((\d+(?:\.\d+)?),\s*(\d+(?:\.\d+)?)%,\s*(\d+(?:\.\d+)?)%\)/);
  if (match) {
    return {
      h: parseFloat(match[1]),
      s: parseFloat(match[2]),
      l: parseFloat(match[3]),
    };
  }
  return { h: 0, s: 0, l: 0 };
};
const generatePastelColor = (index: number, existingHSL: Array<{ h: number; s: number; l: number }>): string => {
  const goldenRatio = 0.618033988749895;
  const existingHues = existingHSL.map((hsl) => hsl.h);

  let hue = (index * goldenRatio * 360) % 360;
  let attempts = 0;
  const maxAttempts = 50;

  // Try to find a hue that's sufficiently different from existing colors
  while (attempts < maxAttempts) {
    const currentHue = hue;
    const minDistance = Math.min(
      ...existingHues.map((existingHue) => {
        const distance = Math.abs(currentHue - existingHue);
        return Math.min(distance, 360 - distance); // Circular distance
      }),
    );

    // If the minimum distance is greater than 30 degrees, we're good
    if (minDistance > 30 || existingHues.length === 0) {
      break;
    }

    // Otherwise, try a different hue
    hue = (hue + 137.5) % 360; // Use golden angle for better distribution
    attempts++;
  }

  const saturation = 60 + (index % 3) * 7.5; // Vary between 60-75%
  const lightness = 70 + (index % 4) * 5; // Vary between 70-85%
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

/**
 * Generates chart colors using predefined graphics colors and generating additional pastel colors as needed.
 *
 * @param {number} count - Number of colors needed
 * @param {Graphics} graphics - Graphics color palette object
 * @return {string[]} Array of color strings
 */
export const generateChartColors = (count: number, graphics?: Graphics): string[] => {
  // Get all predefined colors from graphics
  const predefinedColors = Object.values(graphics || {});
  const predefinedHSL = predefinedColors.map((color) => parseHSL(color));

  // If we have enough predefined colors, return only what's needed
  if (count <= predefinedColors.length) {
    return predefinedColors.slice(0, count);
  }

  // Generate additional colors, accumulating HSL values as we go
  const generatedColors: string[] = [];
  const generatedHSL: Array<{ h: number; s: number; l: number }> = [];
  const colorsNeeded = count - predefinedColors.length;

  for (let i = 0; i < colorsNeeded; i++) {
    // Pass both predefined HSL AND already generated HSL
    const newColor = generatePastelColor(i, [...predefinedHSL, ...generatedHSL]);
    generatedColors.push(newColor);

    // Extract HSL from generated color string
    const parsed = parseHSL(newColor);
    generatedHSL.push(parsed);
  }

  return [...predefinedColors, ...generatedColors];
};

export default {
  generatePastelColor,
  parseHSL,
  generateChartColors,
};
